import {
    DYNAMIC_FEATURE_ID_FIELD_NAME,
    DYNAMIC_FEATURE_X_Y_FIELD_NAME,
    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES,
    TRANSFORM_RANGE_ERROR,
    UNKNOWN_TRANSFORM_ERROR,
} from "./constants";
import { GeoJSON, TileLayer, WMSTileLayer } from "react-leaflet";
import { applyTransform, geoJSONUtil } from "./utils";
import { useMemo, useRef } from "react";

import L from "leaflet";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import { VectorPolyline } from "./VectorPolyline";
import { useGeomanConfig } from "./hooks";
import { useSnackbar } from "../../hooks";
import { useWatch } from "react-hook-form";

export const MapLayout = ({
    blueRectangleData,
    displayToggles,
    geoJsonKey,
    isLiteral,
    orthoimageKey,
    zoomToOrthoimage,
    parcellaireDispatch,
    parcellaireState,
    plotColor,
    transformationToggles,
    selectedTileLayer,
    setCoordMarkerMode,
    setGeoJsonKey,
    onSoilDrawRef,
    setIsDrawingSoil,
    soilPolygonsRef,
    onRadioMetricTargetDrawRef,
    setIsDrawingRadioMetricTarget,
    radioMetricPolygonsRef,
    radioMetricTargetName,
    isRulerActive,
    setIsRulerActive,
    openScaleDialog,
    setOpenScaleDialog,
}) => {
    const { openSnackbar } = useSnackbar();
    const isRotatingRef = useRef(false);
    const parcellaireStateRef = useRef(parcellaireState);
    parcellaireStateRef.current = parcellaireState;
    useGeomanConfig({
        isRotatingRef,
        parcellaireStateRef,
        parcellaireDispatch,
        setCoordMarkerMode,
        setGeoJsonKey,
        onSoilDrawRef,
        setIsDrawingSoil,
        soilPolygonsRef,
        onRadioMetricTargetDrawRef,
        setIsDrawingRadioMetricTarget,
        radioMetricTargetPolygonRef: radioMetricPolygonsRef,
        radioMetricTargetName,
        zoomToOrthoimage,
        isRulerActive,
        setIsRulerActive,
        openScaleDialog,
        setOpenScaleDialog,
    });

    const selectedMetadata = useWatch({ name: "selectedMetadata" });

    const [displayBlue, hiddenPlotMode] = useMemo(() => {
        const toggledDisplays = [false, false];
        displayToggles.forEach((toggleButton) => {
            if (
                toggleButton ===
                GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.DISPLAY_BLUE_MODE
            )
                toggledDisplays[0] = true;
            if (
                toggleButton ===
                GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.HIDDEN_PLOT_MODE
            )
                toggledDisplays[1] = true;
        });
        return toggledDisplays;
    }, [displayToggles]);

    const blueRectangleOnFeature = (_, layer) => {
        layer.setStyle({
            color: "black",
            opacity: 0.1,
            interactive: false,
        });
        layer.bringToBack();
    };

    const geoJsonOnEachFeature = (feature, layer) => {
        layer.setStyle({ color: plotColor });
        if (parcellaireState.selectedLayers.length) {
            const style = parcellaireState.selectedLayers.find(
                (selectedLayer) =>
                    geoJSONUtil.getIdOfFeature(selectedLayer.feature) ===
                    geoJSONUtil.getIdOfFeature(feature)
            )
                ? {
                      color: plotColor,
                      opacity: 1.0,
                      interactive: true,
                  }
                : {
                      color: "black",
                      opacity: 0.4,
                      interactive: false,
                  };

            layer.setStyle(style);
        } else if (
            parcellaireState.lockedLayers.find(
                (lockedLayer) =>
                    geoJSONUtil.getIdOfFeature(lockedLayer.feature) ===
                    geoJSONUtil.getIdOfFeature(feature)
            )
        ) {
            layer.setStyle({
                color: "black",
                opacity: 1.0,
                interactive: false,
            });
        }

        // sets layer color to white if it has been moved
        if (
            parcellaireState.transformModeVectors.find(
                (vector) =>
                    vector.shapeObjectId === geoJSONUtil.getIdOfFeature(feature)
            )
        ) {
            layer.setStyle({ color: "white" });
        }
        const standardProps = [
            "name",
            "plot_id",
            "x",
            "y",
            "crop",
            "experiment",
            "TRANSFORM_TOOL_ID",
        ];
        const customPropsHtml = Object.entries(feature.properties)
            .filter(([key]) => !standardProps.includes(key))
            .map(([key, value]) => `<br/>${key}: ${value}`)
            .join("");

        const scale =
            parcellaireState.scaleMap[
                feature.properties[DYNAMIC_FEATURE_ID_FIELD_NAME]
            ];
        layer.bindPopup(
            `Name: ${feature.properties.name ?? feature.properties.plot_id ?? `X${feature.properties.x}_Y${feature.properties.y}`}
            <br/>X: ${feature.properties.x}
            <br/>Y: ${feature.properties.y}
            ${feature.properties.crop ? `<br/>Crop: ${feature.properties.crop}` : ""}
            ${feature.properties.experiment ? `<br/>Experiment: ${feature.properties.experiment}` : ""}
            ${scale != null ? `<br/>Scale W: ${Math.round(scale.width)}% | H: ${Math.round(scale.height)}%` : ""}
            ${customPropsHtml}
            `
        );

        // triggers when the layer is moved (skip during rotation — handled separately)
        layer.on("pm:edit", () => {
            if (isRotatingRef.current) return;
            const currentState = parcellaireStateRef.current;
            const shapeObjectId = geoJSONUtil.getIdOfFeature(feature);

            const oldFeature = geoJSONUtil.findFeatureById(
                currentState.checkpointGeoJsonData,
                shapeObjectId
            );
            const [oldCenterLongitude, oldCenterLatitude] =
                geoJSONUtil.getCenterOfFeature(oldFeature);
            const { lat: newCenterLatitude, lng: newCenterLongitude } =
                layer.getCenter();

            const vector = {
                shapeObjectId,
                longitude: newCenterLongitude - oldCenterLongitude,
                latitude: newCenterLatitude - oldCenterLatitude,
            };

            // shapeObjectId, vector, toggles, setTransformError are required
            const vectorIndex = currentState.transformModeVectors.findIndex(
                (vector) => vector.shapeObjectId === shapeObjectId
            );

            const newTransformModeVectors =
                vectorIndex === -1
                    ? currentState.transformModeVectors.concat(vector)
                    : currentState.transformModeVectors.toSpliced(
                          vectorIndex,
                          1,
                          vector
                      );

            try {
                const transform = applyTransform(
                    currentState,
                    transformationToggles,
                    newTransformModeVectors
                );

                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.PM_EDIT,
                    transform,
                });
            } catch (error) {
                // TODO: error boundary https://hiphen.atlassian.net/browse/HCC-822
                openSnackbar(
                    error instanceof RangeError
                        ? TRANSFORM_RANGE_ERROR
                        : UNKNOWN_TRANSFORM_ERROR,
                    "error"
                );
            }

            setGeoJsonKey(new Date().getTime());
        });
    };

    const geoJsonPointToLayer = (feature, latlng) => {
        // this transforms markers for points into circle features (that cannot be interacted with, for now)
        return feature.id
            ? L.circleMarker(latlng, {
                  interactive: false,
                  radius: 5,
              }).bindTooltip(feature.id, {
                  permanent: true,
                  direction: "top",
              })
            : L.circleMarker(latlng, {
                  interactive: false,
                  radius: 5,
              });
    };

    const geoJsonTextPointToLayer = (geoJsonPoint, latlng) => {
        return L.marker(latlng, {
            icon: L.divIcon({
                className: "",
                html: `<b style="display:inline-block; transform:translate(-50%, -50%); color: white;text-shadow:-1px -1px 0 #000,1px -1px 0 #000,-1px 1px 0 #000,1px 1px 0 #000;">
                ${
                    selectedMetadata.id === DYNAMIC_FEATURE_X_Y_FIELD_NAME
                        ? `${geoJsonPoint.properties.x}_${geoJsonPoint.properties.y}`
                        : (geoJsonPoint.properties[selectedMetadata.id] ?? "")
                }
                </b>`,
                iconSize: "auto",
            }),
            customType: "idLabel",
            interactive: false,
        });
    };

    return (
        <>
            <TileLayer
                url={selectedTileLayer.url}
                minZoom={2}
                maxNativeZoom={isLiteral ? 19 : 17}
                maxZoom={25}
            />
            {parcellaireState.transformModeVectors.map((vector) => (
                <VectorPolyline
                    key={vector.shapeObjectId}
                    vector={vector}
                    checkpointGeoJsonData={
                        parcellaireState.checkpointGeoJsonData
                    }
                />
            ))}
            {displayBlue && blueRectangleData && (
                <GeoJSON
                    key={`${geoJsonKey}-blueRectangle`}
                    data={blueRectangleData}
                    bubblingMouseEvents={false}
                    onEachFeature={blueRectangleOnFeature}
                    pointToLayer={geoJsonPointToLayer}
                />
            )}
            {!hiddenPlotMode && parcellaireState.transformedGeoJson && (
                <>
                    <GeoJSON
                        key={geoJsonKey}
                        data={parcellaireState.transformedGeoJson}
                        bubblingMouseEvents={false}
                        onEachFeature={geoJsonOnEachFeature}
                        pointToLayer={geoJsonPointToLayer}
                    />
                    {selectedMetadata && (
                        <GeoJSON
                            key={`${geoJsonKey}-text`}
                            data={{
                                type: "FeatureCollection",
                                features:
                                    parcellaireState.transformedGeoJson.features.map(
                                        (feature) => ({
                                            type: "Feature",
                                            geometry: {
                                                type: "Point",
                                                coordinates:
                                                    geoJSONUtil.getCenterOfFeature(
                                                        feature
                                                    ),
                                            },
                                            properties: feature.properties,
                                        })
                                    ),
                            }}
                            pointToLayer={geoJsonTextPointToLayer}
                        />
                    )}
                </>
            )}
            {orthoimageKey && (
                <WMSTileLayer
                    key={orthoimageKey}
                    layers={orthoimageKey}
                    url={process.env.REACT_APP_GEOSERVER_URL}
                    format="image/png"
                    transparent
                    opacity={1}
                    maxZoom={25}
                    tiled
                />
            )}
        </>
    );
};
